import { FC, useContext, useEffect, useState } from 'react';

import {
  AlertIcon,
  Button,
  Input,
  Preloader,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { ALL_LETTERS_AND_DIGITS_REGEXP } from 'consts';
import { LocalizationContext } from 'localization';

import { Comment } from './Comment/Comment';
import s from './Comments.module.scss';
import { ICommentsProps, IFormValues } from './interfaces';
import { useComments } from './useComments';

export const Comments: FC<ICommentsProps> = ({ entityId, type }) => {
  const { t } = useContext(LocalizationContext);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormValues>({
    mode: 'onChange',
    delayError: 150,
    defaultValues: {
      text: '',
    },
  });

  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isShowImportant, setIsShowImportant] = useState<boolean>(false);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const {
    comments,
    commentsLoading,
    deleteComment,
    createComment,
    updateComment,
    updateLoading,
  } = useComments(entityId, type);

  const inputText = watch('text');

  useEffect(() => {
    if (inputText) {
      setIsShowImportant(true);
    } else {
      setIsChecked(false);
      setIsShowImportant(false);
    }
  }, [inputText]);

  return (
    <div className={cn(s.container, s[type])}>
      <Preloader
        loading={commentsLoading || updateLoading}
        hasData={comments.length > 0 && permissions?.has('comment_read')}
        noDataPlaceholder={t('comments.placeholder')}
      >
        <div className={s['comments-container']}>
          {comments.map((comment) => (
            <Comment
              key={`${type}-${entityId}-comment-${comment.id}`}
              comment={comment}
              deleteComment={deleteComment}
              updateComment={updateComment}
              permissions={permissions}
            />
          ))}
        </div>
      </Preloader>
      {permissions?.has('comment_create') && (
        <form
          className={s.form}
          onSubmit={handleSubmit(({ text }) =>
            createComment(text, isChecked).then(() => reset())
          )}
        >
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title={t('comment')}
                error={error?.message}
                disabled={isSubmitting}
                placeholder={t(
                  type === 'CLIENT'
                    ? 'comments.client.placeholder'
                    : 'comments.host.placeholder'
                )}
                rightContent={
                  isShowImportant ? (
                    <div
                      className={s.important}
                      onClick={() => {
                        setIsChecked((prev) => !prev);
                      }}
                    >
                      <Tooltip
                        contentClassName={s.tooltip}
                        position="top"
                        content={
                          !isChecked
                            ? t('important.check')
                            : t('important.check.non')
                        }
                      >
                        <AlertIcon
                          color={!isChecked ? 'secondary' : 'warning'}
                        />
                      </Tooltip>
                    </div>
                  ) : undefined
                }
              />
            )}
            name="text"
            control={control}
            rules={{
              maxLength: {
                value: 255,
                message: t('errors.maxLength', 255),
              },
              validate: (value) => {
                if (!value) {
                  return undefined;
                }

                if (!ALL_LETTERS_AND_DIGITS_REGEXP.test(value)) {
                  return t('errors.notOnlySpecial');
                }
              },
            }}
          />
          <Button
            type="submit"
            disabled={!isDirty || isSubmitting}
            className={s['send-button']}
            iconName="SendIcon"
          />
        </form>
      )}
    </div>
  );
};
